import { User } from '@/store/user/types';

import { getAllCookies, removeCookie, setCookie } from './cookies';
import { futureDate } from './futureDate';

export const ACCEPT_GDPR_COOKIES_KEY = 'has-agreed-to-cookies';
export const DECLINE_GDPR_COOKIES_KEY = 'has-declined-cookies';
export const CALM_LIMITED_DATA_USE = 'calm-limited-data-use';
export const REQUIRES_GDPR_COOKIE_KEY = 'requires-gdpr';

const CALM_DOMAIN = 'calm.com';

export const CALM_AUTH_METHOD = 'calm-auth-method';
export const CalmAuthMethods = {
	Email: 'email',
	Apple: 'apple',
	Facebook: 'facebook',
	Google: 'google',
} as const;
export type CalmAuthMethods = (typeof CalmAuthMethods)[keyof typeof CalmAuthMethods];

export const connectionByAuthMethod: Record<CalmAuthMethods, string> = {
	[CalmAuthMethods.Email]: 'Calm-Username-Password-Authentication',
	[CalmAuthMethods.Apple]: 'apple-calm',
	[CalmAuthMethods.Facebook]: 'facebook-calm',
	[CalmAuthMethods.Google]: 'google-calm',
};

export const CALM_DEVICE_ID = 'x-device-id';
export const CALM_USER_ID = 'calm-user-id';
export const CALM_USER_TOKEN = 'calm-user-token';
export const CALM_IS_HIPAA_USER = 'calm-is-hipaa-user';

export const CALM_USER_COOKIE_KEYS = [CALM_DEVICE_ID, CALM_USER_ID, CALM_USER_TOKEN, CALM_IS_HIPAA_USER];

const cookieDomains = [
	'',
	'.local.calm.com',
	'local.calm.com',
	'.calm.com',
	CALM_DOMAIN,
	'www.calm.com',
	'.www.calm.com',
	'.googleadservices.com',
	'.ranksci.com',
	'.app.link',
	'.youtube.com',
	'.google.com',
];

export const acceptGdprCookies = (): void => {
	setCookie(ACCEPT_GDPR_COOKIES_KEY, 'true', {
		expires: futureDate(10, 'years'),
	});
	removeCookie(DECLINE_GDPR_COOKIES_KEY);
	removeCookie(CALM_LIMITED_DATA_USE, { domain: CALM_DOMAIN });
	removeCookie(REQUIRES_GDPR_COOKIE_KEY);
};

export const declineGdprCookies = (): void => {
	setCookie(ACCEPT_GDPR_COOKIES_KEY, 'false', {
		expires: futureDate(10, 'years'),
	});
	setCookie(DECLINE_GDPR_COOKIES_KEY, 'true', {
		expires: futureDate(10, 'years'),
	});
	removeCookie(CALM_LIMITED_DATA_USE, { domain: CALM_DOMAIN }); // We want to have either limited data or GDPR cookies, not both
	removeCookie(REQUIRES_GDPR_COOKIE_KEY);
};

export const declineOptInCookies = (): void => {
	setCookie(CALM_LIMITED_DATA_USE, 'true', {
		expires: futureDate(10, 'years'),
		domain: CALM_DOMAIN,
	});
	removeCookie(DECLINE_GDPR_COOKIES_KEY);
	removeCookie(ACCEPT_GDPR_COOKIES_KEY);
	removeCookie(REQUIRES_GDPR_COOKIE_KEY);
};

export const acceptOptInCookies = (): void => {
	removeCookie(CALM_LIMITED_DATA_USE, { domain: CALM_DOMAIN });
	removeCookie(DECLINE_GDPR_COOKIES_KEY);
	removeCookie(ACCEPT_GDPR_COOKIES_KEY);
	removeCookie(REQUIRES_GDPR_COOKIE_KEY);
};

export const setRequiresGdprCookie = (requiresGdpr: boolean): void => {
	setCookie(REQUIRES_GDPR_COOKIE_KEY, String(requiresGdpr), {
		expires: futureDate(1, 'hours'), // Short-lived cache
	});
};

export const clearClassicUserCookies = (): void => {
	removeCookie(CALM_USER_TOKEN);
	removeCookie(CALM_USER_ID);
	removeCookie(CALM_IS_HIPAA_USER);
};

export const setUserCookies = (user: User, authMethod?: CalmAuthMethods): void => {
	if (typeof user?.token === 'string') {
		setCookie(CALM_USER_TOKEN, user.token);
	}
	if (typeof user?.id === 'string') {
		setCookie(CALM_USER_ID, user.id);
	}
	if (user?.is_hipaa_compliant) {
		setCookie(CALM_IS_HIPAA_USER, String(user.is_hipaa_compliant));
	}
	if (user?.has_opted_in_limited_data_use) {
		setCookie(CALM_LIMITED_DATA_USE, String(user.has_opted_in_limited_data_use), { domain: CALM_DOMAIN });
	}
	if (authMethod) {
		setCookie(CALM_AUTH_METHOD, String(authMethod), { expires: 1 });
	}
};

export const removeAllCookies = (willKeepLoginCookies = true): void => {
	Object.keys(getAllCookies() || {}).forEach(cookie => {
		// Only remove cookies if we don't want to keep login cookies or if they aren't login cookies
		if (!(willKeepLoginCookies && CALM_USER_COOKIE_KEYS.includes(cookie))) {
			cookieDomains.forEach(domain => {
				removeCookie(cookie, { domain });
			});
		}
	});
};

export const isHomePageCookies = (pathname: string) => {
	return pathname === '/';
};
